import api from './authService';

export const getRecordings = () => {
  return api.get('/recordings');
};

export const getRecording = (id) => {
  return api.get(`/recordings/${id}`);
};

export const startRecording = (data) => {
  return api.post('/recordings/start', data);
};

export const stopRecording = (id, audioBlob) => {
  const formData = new FormData();
  formData.append('file', audioBlob, 'recording.webm');
  
  return api.post(`/recordings/${id}/stop`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
};

export const updateRecording = (id, data) => {
  return api.put(`/recordings/${id}`, data);
};

export const deleteRecording = (id) => {
  return api.delete(`/recordings/${id}`);
};

/**
 * Recording Manager - Handles MediaRecorder and audio stream
 */
export class RecordingManager {
  constructor() {
    this.mediaRecorder = null;
    this.audioStream = null;
    this.audioChunks = [];
    this.startTime = null;
    this.recordingId = null;
  }

  /**
   * Request microphone access and initialize recording
   * @param {MediaStream} existingStream - Optional existing media stream to use
   */
  async initialize(existingStream = null) {
    try {
      // Use existing stream if provided, otherwise request new one
      if (existingStream) {
        this.audioStream = existingStream;
      } else {
        // Request audio permission
        this.audioStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true
          }
        });
      }

      // Check supported MIME types
      const mimeType = this.getSupportedMimeType();
      
      // Create MediaRecorder with the audio stream
      this.mediaRecorder = new MediaRecorder(this.audioStream, {
        mimeType,
        audioBitsPerSecond: 128000 // 128 kbps
      });

      // Set up event handlers
      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.audioChunks.push(event.data);
        }
      };

      return true;
    } catch (error) {
      console.error('Error initializing recording:', error);
      throw new Error('Failed to access microphone. Please grant permission.');
    }
  }

  /**
   * Get the best supported MIME type for recording
   */
  getSupportedMimeType() {
    const types = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/ogg;codecs=opus',
      'audio/mp4'
    ];

    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }

    return ''; // Use default
  }

  /**
   * Start recording
   */
  start() {
    if (!this.mediaRecorder) {
      throw new Error('MediaRecorder not initialized');
    }

    this.audioChunks = [];
    this.startTime = Date.now();
    this.mediaRecorder.start(1000); // Collect data every second
  }

  /**
   * Stop recording and return the audio blob
   */
  async stop() {
    return new Promise((resolve, reject) => {
      if (!this.mediaRecorder) {
        reject(new Error('MediaRecorder not initialized'));
        return;
      }

      this.mediaRecorder.onstop = () => {
        try {
          const audioBlob = new Blob(this.audioChunks, {
            type: this.mediaRecorder.mimeType || 'audio/webm'
          });
          resolve(audioBlob);
        } catch (error) {
          reject(error);
        }
      };

      this.mediaRecorder.stop();
    });
  }

  /**
   * Get current recording duration in seconds
   */
  getDuration() {
    if (!this.startTime) return 0;
    return Math.floor((Date.now() - this.startTime) / 1000);
  }

  /**
   * Check if currently recording
   */
  isRecording() {
    return this.mediaRecorder && this.mediaRecorder.state === 'recording';
  }

  /**
   * Clean up resources
   * @param {boolean} stopStream - Whether to stop the audio stream tracks (default: false)
   */
  cleanup(stopStream = false) {
    if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
      this.mediaRecorder.stop();
    }
    
    // Only stop stream if explicitly requested (in case we're using an external stream)
    if (stopStream && this.audioStream) {
      this.audioStream.getTracks().forEach(track => track.stop());
      this.audioStream = null;
    }
    
    this.mediaRecorder = null;
    this.audioChunks = [];
    this.startTime = null;
  }
}
